import { useState, useEffect, useMemo } from 'react';
import { cargoListDataService, type CargoListDataItem } from '@/services/cargoListDataService';
import type { AutocompleteOption } from '@/components/ui/Autocomplete';

interface SelectOption {
  value: string | number;
  label: string;
}

export function useCargoListFormOptions() {
  // State for autocomplete data (loaded dynamically)
  const [charterers, setCharterers] = useState<CargoListDataItem[]>([]);
  const [brokers, setBrokers] = useState<CargoListDataItem[]>([]);
  const [rateAssessors, setRateAssessors] = useState<CargoListDataItem[]>([]);
  const [trades, setTrades] = useState<CargoListDataItem[]>([]);

  // State for select data (loaded once)
  const [privacyLevels, setPrivacyLevels] = useState<CargoListDataItem[]>([]);
  const [fixtureTypes, setFixtureTypes] = useState<CargoListDataItem[]>([]);
  const [fixtureStatuses, setFixtureStatuses] = useState<CargoListDataItem[]>([]);
  const [certaintyCat, setCertaintyCategories] = useState<CargoListDataItem[]>([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load all form data once on mount
  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      setError(null);

      try {
        const [
          privacyLevelsData,
          fixtureTypesData,
          fixtureStatusesData,
          dataCertaintyData,
          charterersData,
          brokersData,
          rateAssessorsData,
          tradesData,
        ] = await Promise.all([
          cargoListDataService.getPrivacyLevels(),
          cargoListDataService.getFixtureTypes(),
          cargoListDataService.getFixtureStatuses(),
          cargoListDataService.getDataCertaintyCategories(),
          cargoListDataService.getCharterers(),
          cargoListDataService.getBrokers(),
          cargoListDataService.getRateAssessors(),
          cargoListDataService.getTrades(),
        ]);

        setPrivacyLevels(privacyLevelsData);
        setFixtureTypes(fixtureTypesData);
        setFixtureStatuses(fixtureStatusesData);
        setCertaintyCategories(dataCertaintyData);

        // Load full lists once. The API returns a manageable size and Autocomplete
        // filters them client-side, so there is nothing to fetch per keystroke.
        // Capping created bugs where an unchanged FK option outside the cap was
        // lost on save.
        setCharterers(charterersData);
        setBrokers(brokersData);
        setRateAssessors(rateAssessorsData);
        setTrades(tradesData);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to load initial form data';
        console.error('Failed to load initial form data:', err);
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, []);

  // Convert API data to autocomplete options
  const chartererOptions = useMemo((): AutocompleteOption[] => {
    return charterers.map((item) => ({
      id: item.Id,
      name: item.Name,
      label: item.Name,
    }));
  }, [charterers]);

  const brokerOptions = useMemo((): AutocompleteOption[] => {
    return brokers.map((item) => ({
      id: item.Id,
      name: item.Name,
      label: item.Name,
    }));
  }, [brokers]);

  const rateAssessorOptions = useMemo((): AutocompleteOption[] => {
    return rateAssessors.map((item) => ({
      id: item.Id,
      name: item.Name,
      label: item.Name,
    }));
  }, [rateAssessors]);

  const tradeOptions = useMemo((): AutocompleteOption[] => {
    return trades.map((item) => ({
      id: item.Id,
      name: item.Name,
      label: item.Name,
    }));
  }, [trades]);

  // Convert API data to select options
  const privacyLevelOptions = useMemo((): SelectOption[] => {
    return privacyLevels.map((item) => ({
      value: item.Id,
      label: item.Name,
    }));
  }, [privacyLevels]);

  const fixtureTypeOptions = useMemo((): SelectOption[] => {
    return fixtureTypes.map((item) => ({
      value: item.Id,
      label: item.Name,
    }));
  }, [fixtureTypes]);

  const fixtureStatusOptions = useMemo((): SelectOption[] => {
    return fixtureStatuses.map((item) => ({
      value: item.Id,
      label: item.Name,
    }));
  }, [fixtureStatuses]);

  const certaintyCategoryOptions = useMemo((): SelectOption[] => {
    return certaintyCat.map((item) => ({
      value: item.Id,
      label: item.Name,
    }));
  }, [certaintyCat]);

  return {
    // Autocomplete options - filtering is client-side, see note above
    chartererOptions,
    brokerOptions,
    rateAssessorOptions,
    tradeOptions,

    // Select options
    privacyLevelOptions,
    fixtureTypeOptions,
    fixtureStatusOptions,
    certaintyCategoryOptions,

    // Loading and error states
    loading,
    error,
  };
}
